import { useIM } from '@wui/im'
import { storeToRefs } from 'pinia'


import { useCallSessionRoomStore } from '@/entities/call-session'
import { useCallSessionMediaStore } from '@/entities/call-session-media'
import { useStoreConferenceMediaStatuses } from '@/entities/conference-participant'
import { useStoreMediaDevices } from '@/entities/media-devices'
import { useWebRTC, SCREEN_SHARING_STREAM_NAME, WEBCAM_STREAM_NAME } from '@/entities/webrtc'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useGuestAuth } from '@/shared/composables/guest-auth'
import { emitter } from '@/shared/services/emitter'
import { EmitterEvents } from '@/shared/types/emitter-events'
import { fromPServedUser } from '@/shared/utils/uri'
import { applyScreenShareSenderParamsWithRetry } from '@/shared/utils/webrtc'

import {
  activateUserMediaStream,
  closePictureInPicture,
  deactivateLocalVideoStream,
  getTransceiverMidByTrackId,
  openPictureInPicture,
} from './local-media'

export const useConferenceMedia = () => {
  return {
    switchCameraInConferenceSession,
    switchScreenSharingInConferenceSession,
  }
}

export const switchCameraInConferenceSession = async () => {
  const facade = useWebRTC().currentRTCSessionFacade
  const { currentUser } = useCurrentUser()
  const { currentGuest } = useGuestAuth()
  const currentUserPServed = currentUser.value?.imLogin ?? currentGuest.value?.pServed
  const currentUserNumber = currentUser.value?.internalNumber
    ?? (currentGuest.value ? fromPServedUser(currentGuest.value.pServed) : undefined)

  if (!facade.value || !currentUserPServed || !currentUserNumber) {
    throw new Error('No valid session for user found')
  }

  const { muteParticipantTrackInConference, unmuteParticipantTrackInConference, addParticipantTrackToConference } = useIM()
  const { getParticipantMediaStatus, getCurrentUserHasWebCamStreamByCallId } = useStoreConferenceMediaStatuses()
  const { webCamLastTrackId } = storeToRefs(useCallSessionMediaStore())
  const { isVideoMuted, localVideoStream } = facade.value
  const room = useCallSessionRoomStore().getRoom(facade.value.sessionId)

  const status = getParticipantMediaStatus(facade.value.callId.value)
  const isVideoMutedNow = status?.isVideoMuted ?? true

  try {
    if (isVideoMutedNow) {
      const track = await activateUserMediaStream(facade.value.session, localVideoStream, 'video', webCamLastTrackId.value)
      // Prefer the live session track — deferred blur upgrade may already have swapped it.
      webCamLastTrackId.value = localVideoStream.value?.getVideoTracks()[0]?.id ?? track?.id ?? ''
      if (!getCurrentUserHasWebCamStreamByCallId(facade.value.callId.value)) {
        await addParticipantTrackToConference({
          pServed: room.pServedUser,
          internalNumber: currentUserNumber,
          kind: 'video',
          name: WEBCAM_STREAM_NAME,
          callId: facade.value.callId.value,
        })
      } else {
        await unmuteParticipantTrackInConference({
          pServed: room.pServedUser,
          internalNumber: currentUserNumber,
          name: WEBCAM_STREAM_NAME,
          callId: facade.value.callId.value,
        })
      }
    } else {
      const videoTrack = localVideoStream.value?.getVideoTracks().at(0)
      if (videoTrack) {
        await muteParticipantTrackInConference({
          pServed: room.pServedUser,
          internalNumber: currentUserNumber,
          name: WEBCAM_STREAM_NAME,
          callId: facade.value.callId.value,
        })
      }
      deactivateLocalVideoStream(localVideoStream, facade.value.session.connection)
    }
    isVideoMuted.value = !isVideoMutedNow
  } catch (error) {
    return Promise.reject(error)
  }
}

export const switchScreenSharingInConferenceSession = async () => {
  const facade = useWebRTC().currentRTCSessionFacade
  const { currentUser } = useCurrentUser()
  const { currentGuest } = useGuestAuth()

  const currentUserPServed = currentUser.value?.imLogin ?? currentGuest.value?.pServed
  const currentUserNumber = currentUser.value?.internalNumber
    ?? (currentGuest.value ? fromPServedUser(currentGuest.value.pServed) : undefined)

  if (!facade.value || !currentUserPServed || !currentUserNumber) {
    throw new Error('No valid session for user found')
  }

  const { localScreenStream, callId } = facade.value
  const room = useCallSessionRoomStore().getRoom(facade.value.sessionId)
  const { getParticipantMediaStatus, getCurrentUserHasScreenSharingStreamByCallId } = useStoreConferenceMediaStatuses()
  const { screenSharingLastTrackId, screenSharingLastSenderMid } = storeToRefs(useCallSessionMediaStore())
  const status = getParticipantMediaStatus(facade.value.callId.value)
  const isScreenMutedNow = status?.isScreenSharingMuted ?? true
  const { addParticipantTrackToConference, muteParticipantTrackInConference, unmuteParticipantTrackInConference } = useIM()
  const rememberScreenShareSenderMid = (trackId?: string) => {
    const mid = getTransceiverMidByTrackId(facade.value?.session.connection, trackId)
    if (mid) {
      screenSharingLastSenderMid.value = mid
    }
  }

  try {
    if (isScreenMutedNow) {
      const track = await activateUserMediaStream(
        facade.value.session,
        localScreenStream,
        'screensharing',
        screenSharingLastTrackId.value,
        screenSharingLastSenderMid.value,
      )
      screenSharingLastTrackId.value = track?.id ?? ''
      rememberScreenShareSenderMid(track?.id)
      if (track) {
        track.addEventListener('ended', () => {
          const activeFacade = useWebRTC().currentRTCSessionFacade.value
          const currentTrack = activeFacade?.localScreenStream.value?.getVideoTracks().at(0)
          if (!currentTrack || currentTrack.id !== track.id) {
            return
          }

          switchScreenSharingInConferenceSession()
        }, { once: true })
      }

      if (!getCurrentUserHasScreenSharingStreamByCallId(facade.value.callId.value ?? '')) {
        await addParticipantTrackToConference({
          pServed: room.pServedUser,
          internalNumber: currentUserNumber,
          kind: 'video',
          name: SCREEN_SHARING_STREAM_NAME,
          callId: callId.value,
        })
      } else {
        await unmuteParticipantTrackInConference({
          pServed: room.pServedUser,
          internalNumber: currentUserNumber,
          name: SCREEN_SHARING_STREAM_NAME,
          callId: facade.value.callId.value,
        })
      }

      if (track) {
        void applyScreenShareSenderParamsWithRetry(
          facade.value.session.connection,
          track.id,
          useStoreMediaDevices().screenShareQuality,
        ).then(() => rememberScreenShareSenderMid(track.id))
      }

      openPictureInPicture()
    } else {
      closePictureInPicture()
      const videoTrack = localScreenStream.value?.getVideoTracks().at(0)
      rememberScreenShareSenderMid(videoTrack?.id)
      if (videoTrack) {
        await muteParticipantTrackInConference({
          pServed: room.pServedUser,
          internalNumber: currentUserNumber,
          name: SCREEN_SHARING_STREAM_NAME,
          callId: facade.value.callId.value,
        })
      }
      deactivateLocalVideoStream(localScreenStream, facade.value.session.connection)
    }
  } catch (error) {
    return Promise.reject(error)
  }
}

emitter.on(EmitterEvents.TOGGLE_MUTE_VIDEO_IN_CONFERENCE, () => {
  switchCameraInConferenceSession()
})

emitter.on(EmitterEvents.TOGGLE_SCREEN_SHARING_IN_CONFERENCE, () => {
  switchScreenSharingInConferenceSession()
})
